
import Vue from 'vue';
import {
  HOTSPOTSIMPLE_SIDEPANEL_CLOSE_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_LEFT_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_RIGHT_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_MOUNTED_EVENT,
  HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS,
  HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS,
} from '@owc-packages/Hotspot/src/Hotspot.config';
import { HotspotSidepanelComponent } from '@owc-packages/Hotspot/src/HotspotSidepanel';
import { mobileBreakpoints } from '@owc-packages/_utils/MediaQueryService';
import { ScrollLockService } from '@workbench/core';
import HotspotSidepanelItem from './HotspotSidepanelItem.vue';
import ResizeEvent from '@owc-utils/Mixins/ResizeEvent';
import AEM from '@owc-utils/Mixins/AEM';

const scrollLockService = new ScrollLockService();

/**
 * TODO: Documentation
 * Hotspot Sidepanel
 *
 * A Sidepanel to be placed in the HotspotSimpleItem.
 */
export default (Vue as HotspotSidepanelComponent).extend({
  name: 'owc-hotspot-sidepanel',
  mixins: [ResizeEvent, AEM],
  props: {
    sidePanelOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    hotspots: {
      type: Array,
      required: true,
      default: () => [],
    },
    activeHotspot: {
      type: Object,
      default: null,
    },
    leftHotspot: {
      type: Object,
      default: null,
    },
    rightHotspot: {
      type: Object,
      default: null,
    },
    isRtl: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HotspotSidepanelItem,
  },
  data: () => ({
    isMobile: false,
    isBackwardsAnimation: false,
  }),
  mounted() {
    this.onMQChange?.(this.setIsMobileFlag);
    this.setIsMobileFlag();
    this.$emit(HOTSPOTSIMPLE_SIDEPANEL_MOUNTED_EVENT);
    if (this.isMobile) {
      scrollLockService.lock();
      this.$nextTick(() => this.scrollMobileSliderToInitialPage(this.$refs.sidepanelSlider));
    }
  },
  beforeDestroy() {
    if (this.isMobile) {
      scrollLockService.unlock();
    }
  },
  computed: {
    leftNavButtonLabel() {
      return this.leftHotspot ? this.leftHotspot.hotspotText : '';
    },
    rightNavButtonLabel() {
      return this.rightHotspot ? this.rightHotspot.hotspotText : '';
    },
    dotNavCurrentPage() {
      return this.hotspots.findIndex(hotspot => hotspot.id === this.activeHotspot?.id) || 0;
    },
    normalizedCurrentPage() {
      return this.dotNavCurrentPage + 1;
    },
    activeSidepanel() {
      return this.activeHotspot?.sidepanel;
    },
  },
  methods: {
    // Because of the way the tooltip (on the workbench side) is implemented
    // we need to move the hotspot to is correct position first before we
    // activate it.
    // It is necessary to pass the click event to the parent. The parent will handle
    // the activation and diactivation of the correct hotspot item.
    // https://jira.mercedes-benz.io/browse/CINOWF-30484
    activateLeftHotspot() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_LEFT_EVENT);
    },
    activateRightHotspot() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_RIGHT_EVENT);
    },
    closeSidebar() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_CLOSE_EVENT);
    },
    setIsMobileFlag() {
      const currentMq = this.getCurrentMediaQuery?.();
      this.isMobile = Boolean(currentMq && mobileBreakpoints.includes(currentMq));
    },
    scrollMobileSliderToInitialPage(sliderEl) {
      const observer = new MutationObserver(() => {
        const sliderInnerEl: HTMLElement | null = sliderEl.querySelector(
          HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS,
        );
        if (sliderInnerEl) {
          const sliderItemEl = sliderInnerEl.querySelector(
            `${HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS}:nth-child(${this.normalizedCurrentPage})`,
          ) as HTMLElement;
          sliderInnerEl.style.scrollBehavior = 'initial';
          sliderInnerEl.scrollLeft = sliderItemEl?.offsetLeft || 0;
          sliderInnerEl.removeAttribute('style');
          observer.disconnect();
        }
      });
      observer.observe(sliderEl, { childList: true });
    },
    toggleBackwardsAnimation(currentActiveHotspot, previousActiveHotspot) {
      const lastItemIndex = this.hotspots.length - 1;
      const { currentIndex, previousIndex } = this.hotspots.reduce(
        (indexes, hotspot, index) => {
          if (hotspot.id === currentActiveHotspot.id) {
            indexes.currentIndex = index;
          }
          if (hotspot.id === previousActiveHotspot.id) {
            indexes.previousIndex = index;
          }
          return indexes;
        },
        {
          currentIndex: 0,
          previousIndex: 0,
        },
      );

      // Slide from last to first
      if (currentIndex === 0 && previousIndex === lastItemIndex) {
        this.isBackwardsAnimation = true;
        return;
      }

      // Slide from first to last
      if (currentIndex === lastItemIndex && previousIndex === 0) {
        this.isBackwardsAnimation = false;
        return;
      }

      this.isBackwardsAnimation = currentIndex - previousIndex > 0;
    },
    stopVideoWhenSlidingOnMobile() {
      const wrapper = this.$refs.sidepanelSlider;
      const playButton = wrapper.querySelector('.plyr--playing [data-plyr="play"]') as HTMLButtonElement;
      if (playButton) {
        playButton.click();
      }
    },
    // OWCA-1099. The idea is as follows: mobileSlideChanged listener is added
    // once to give us the opportunity to register another listener to detect
    // scroll position of the slider; when snap point is reached, which means
    // new slide is fully visible and previous fully hidden, we iterate through
    // scrollable elements and call scrollItemContentToTop() which detects
    // whether slide is fully hidden to call scrollTo() onto it.
    mobileSlideChanged() {
      this.stopVideoWhenSlidingOnMobile();
      const wrapper = this.$refs.sidepanelSlider;
      const sliderInnerEl = wrapper.querySelector(HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS);
      if (sliderInnerEl) {
        sliderInnerEl.addEventListener('scroll', (event: Event): void => {
          const target = event.target as HTMLElement;
          // Snap point is reached, so loop through items and scroll content to top if needed
          if (target.scrollLeft % target.offsetWidth === 0) {
            this.$refs.sidepanelSlider
              .querySelectorAll(HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS)
              .forEach(this.scrollItemContentToTop);
          }
        });
      }
    },
    scrollItemContentToTop(item) {
      if (item.scrollTop > 0) {
        const { left, width } = item.getBoundingClientRect();
        if (left < 0 || left >= width) {
          item.scrollTo(0, 0);
        }
      }
    },
  },
  watch: {
    activeHotspot: 'toggleBackwardsAnimation',
  },
});
